// @flow

import { useCallback, useEffect, useState } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";
import { Modal } from "react-bootstrap";

import { translate } from "../../IntlProviderWrapper";
import { niceNumber } from "../../../lib/Util";
import ErrorComponent from "../../ErrorComponent";
import TranslatedMaterialTitle from "../../TranslatedMaterialTitle.tsx";
import Range from "../../react/Range";
import { CONVERT_ORDER } from "./reducers/linesOrders.tsx";
import { getBackend } from "../../../lib/backend/Backend2";
import { LinesOrdersMaps } from "./reducers/Mappers.tsx";
import { ORDER_STATUS, ORDER_TYPE } from "../../../lib/ManufacturingConsts.generated";
import { LinesOrdersLogic } from "./reducers/BusinessLogic.tsx";

import type { LineOrdersActionsTypes, ConvertOperationData } from "./reducers/linesOrders.tsx";
import type { IOrderModelBase } from "../../../lib/backend/manufacturing2.generated.types";
import type { UseCallbackHook } from "../../../lib/Util";

import "./OrderConversionModal.scss";
import Loader from "../../Loader";

type OrderConversionModalProps = {
    order_external_id: string,
    show: boolean,
    onClose: () => void,
};

type HTMLInputElement = window.HTMLInputElement;

const OrderConversionModal = (props: OrderConversionModalProps) => {
    const { order_external_id, show, onClose } = props;
    const dispatch = useDispatch<LineOrdersActionsTypes>();
    const [operations, setOperations] = useState<IOrderModelBase[]>([]);
    const [production_quantities, setProductionQuantities] = useState<number[]>([]);
    const [production_fraction, setProductionFraction] = useState<number>(0);
    const [error, setError] = useState<string>("");
    const [warning, setWarning] = useState<string>("");
    const [is_loading, setIsLoading] = useState<boolean>(false);

    const initQuantities = (order_operations: IOrderModelBase[]): void => {
        const line_operations_map = new Map(
            [...LinesOrdersMaps.all_orders_map.values()]
                .filter(operation => operation.external_id === order_external_id)
                .map(operation => [operation.uuid, operation])
        );
        let fraction = 0;
        for (const operation of order_operations) {
            const line_operation = line_operations_map.get(operation.uuid);
            if (line_operation !== undefined) {
                operation.quantity_total = line_operation.quantity_total;
            }

            if (LinesOrdersMaps.converted_operations.has(operation.uuid)) {
                const converted_operation_uuid = LinesOrdersLogic.generateConvertedOrderId(operation.uuid);
                const converted_operation = LinesOrdersLogic.getConvertedOperation(converted_operation_uuid);
                if (converted_operation !== null) {
                    if (line_operation === undefined) {
                        // Operation was fully converted
                        operation.quantity_total = 0;
                    }

                    operation.quantity_total += converted_operation.quantity_total;
                    if (operation.quantity_total > 0 && fraction === 0) {
                        fraction = converted_operation.quantity_total / operation.quantity_total;
                    }
                }
            }
        }

        const quantities: number[] = order_operations.map(operation => {
            const converted_operation_uuid = LinesOrdersLogic.generateConvertedOrderId(operation.uuid);
            const converted_operation = LinesOrdersLogic.getConvertedOperation(converted_operation_uuid);
            return converted_operation !== null ? converted_operation.quantity_total : 0;
        });

        setProductionQuantities(quantities);
        setProductionFraction(fraction);
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError("");
            setWarning("");
            setOperations([]);
            setProductionQuantities([]);
            setProductionFraction(0);
            try {
                const res = await getBackend().manufacturing.getOrdersSimple({
                    order_external_id: order_external_id,
                    order_types: [ORDER_TYPE.plan],
                    status: ORDER_STATUS.open,
                });
                const order_operations = res.operations;
                if (order_operations.length === 0) {
                    setWarning(translate("OrderConversionModal.no_operations_found", "No operations found."));
                }

                setOperations(order_operations);
                initQuantities(order_operations);
            } catch (error) {
                console.error("Error while retrieving data from server", error);
                setError(
                    translate(
                        "OrderConversionModal.error_while_retreving_data",
                        "Something went wrong while retrieving data from server."
                    )
                );
            }

            setIsLoading(false);
        };

        fetchData();
    }, [order_external_id]);

    const handleProductionQuantityChange: UseCallbackHook<SyntheticInputEvent<HTMLInputElement>> = useCallback(
        (event: SyntheticInputEvent<HTMLInputElement>) => {
            const index = Number(event.target.name);
            const total_quantity = operations[index] !== undefined ? operations[index].quantity_total : 0;
            const production_quantity = Math.max(0, Math.min(total_quantity, Number(event.target.value)));
            const fraction = total_quantity > 0 ? production_quantity / total_quantity : 0;
            const quantities: number[] = operations.map((operation, i) => {
                return Math.round(operation.quantity_total * fraction);
            });
            setProductionQuantities(quantities);
            setProductionFraction(fraction);
        },
        [production_quantities, operations]
    );

    const handleQuantityFractionChange: UseCallbackHook<SyntheticEvent<HTMLInputElement>> = useCallback(
        (event: SyntheticEvent<HTMLInputElement>) => {
            const fraction = Number((event.currentTarget: HTMLInputElement).value);
            const quantities: number[] = operations.map((operation, i) => {
                return Math.round(operation.quantity_total * fraction);
            });
            setProductionQuantities(quantities);
            setProductionFraction(fraction);
        },
        [production_fraction, production_quantities, operations]
    );

    const handleFormSubmit: UseCallbackHook<SyntheticEvent<HTMLFormElement>> = useCallback(
        (event: SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            handleModalClose();
        }
    );

    const handleModalClose: UseCallbackHook<void> = useCallback(() => {
        initQuantities(operations);
        onClose();
    });

    const handleConvert: UseCallbackHook<SyntheticInputEvent<HTMLInputElement>> = useCallback(() => {
        const operations_data: ConvertOperationData[] = [];
        for (let i = 0; i < operations.length; i++) {
            const operation = operations[i];
            const quantity = production_quantities[i];
            operations_data.push({
                parent: operation,
                quantity,
            });
        }

        if (operations_data.length > 0 && operations_data.length === operations.length) {
            dispatch({
                type: CONVERT_ORDER,
                data: {
                    operations: operations_data,
                },
            });
            handleModalClose();
        }
    });

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            className="order-conversion-modal"
            dialogClassName="modal-dialog-scrollable"
            animation={false}
        >
            <Modal.Header>
                <Modal.Title>
                    {translate("OrderConversionModal.convert_order", "Convert order")} {order_external_id}
                </Modal.Title>
                <button type="button" className="close" onClick={handleModalClose}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                {(error !== "" || warning !== "") && (
                    <div className="order-conversion-modal__alerts">
                        <ErrorComponent className="my-0" type="error" msg={error} />
                        <ErrorComponent className="my-0" type="warning" msg={warning} />
                    </div>
                )}
                {is_loading && <Loader className="order-conversion-modal__loader" />}
                {!is_loading && operations.length > 0 && (
                    <div className="order-conversion-modal__form">
                        <form action="#" onSubmit={handleFormSubmit}>
                            <div className="order-conversion-modal__range form-group">
                                <label htmlFor="order-cenversion-modal-production-fraction-range">
                                    {translate("OrderConversionModal.quantity_distribution", "Quantity distribution")}
                                </label>
                                <Range
                                    id="order-cenversion-modal-production-fraction-range"
                                    min={0}
                                    max={1}
                                    step={0.001}
                                    name="production-fraction"
                                    value={production_fraction}
                                    onChange={handleQuantityFractionChange}
                                />
                                <small
                                    className="form-text text-muted mt-0 d-flex"
                                    id="order-cenversion-modal-production-fraction-description"
                                >
                                    <span className="mr-auto">
                                        {translate("common.production_order", "Production order")}:{" "}
                                        <strong>{niceNumber(production_fraction * 100, 1)}%</strong>
                                    </span>
                                    <span>
                                        {translate("common.planning_order", "Planning order")}:{" "}
                                        <strong>{niceNumber(100 - production_fraction * 100, 1)}%</strong>
                                    </span>
                                </small>
                            </div>
                            {operations.map((operation, index) => {
                                const quantity =
                                    production_quantities[index] !== undefined ? production_quantities[index] : 0;
                                const total_quantity = operation.quantity_total;
                                const id = `order-cenversion-modal-operation-${index}`;
                                return (
                                    <div key={operation.uuid} className="order-conversion-modal__operation form-group">
                                        <dl className="order-conversion-modal__operation-details">
                                            <div>
                                                <dt>{translate("common.operation", "Operation")}:</dt>
                                                <dd>
                                                    <strong>{order_external_id}</strong> - {operation.process_num}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt>{translate("common.material", "Material")}:</dt>
                                                <dd>
                                                    <strong>{operation.material_external_id}</strong> -{" "}
                                                    {
                                                        <TranslatedMaterialTitle
                                                            material_external_id={operation.material_external_id}
                                                            material_title={operation.material_title}
                                                        />
                                                    }
                                                </dd>
                                            </div>
                                            <div>
                                                <dt>{translate("common.quantity", "Quantity")}:</dt>
                                                <dd>
                                                    {niceNumber(total_quantity)} <em>{operation.measurement_unit}</em>
                                                </dd>
                                            </div>
                                        </dl>
                                        <label htmlFor={`${id}-input`}>
                                            {translate(
                                                "OrderConversionModal.production_quantity",
                                                "Production quantity"
                                            )}
                                        </label>
                                        <input
                                            className="form-control"
                                            id={`${id}-input`}
                                            type="number"
                                            name={String(index)}
                                            value={quantity}
                                            min={0}
                                            max={total_quantity}
                                            onChange={handleProductionQuantityChange}
                                            aria-describedby={`${id}-input-description`}
                                        />
                                        <small className="form-text text-muted" id={`${id}-input-description`}>
                                            {translate("OrderConversionModal.planned_quantity", "Planned quantity")}:{" "}
                                            <strong>
                                                {niceNumber(total_quantity - quantity)}{" "}
                                                <em>{operation.measurement_unit}</em>
                                            </strong>
                                        </small>
                                    </div>
                                );
                            })}
                        </form>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" type="button" onClick={handleModalClose}>
                    {translate("common.cancel", "Cancel")}
                </button>
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleConvert}
                    disabled={operations.length === 0}
                >
                    {translate("common.convert", "Convert")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default OrderConversionModal;
